import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { CheckCircle2, XCircle, Mail, Phone, Building2, Globe2, GraduationCap, RefreshCw, Copy, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PendingAgentsPage = () => {
  const { api } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // agent_id currently being processed
  const [tempPwd, setTempPwd] = useState(null); // { agent_id, password, email }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/ebn/admin/agents/pending');
      setAgents(r.data || []);
    } catch {
      setAgents([]);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id, email) => {
    setBusy(id);
    try {
      const r = await api().post(`/ebn/admin/agents/${id}/approve`);
      if (r.data.email_sent) {
        toast.success('Agent approved — welcome email sent.');
      } else {
        toast.warning('Agent approved. Email not sent (SMTP not configured) — share the temp password manually.');
        setTempPwd({ agent_id: id, email, password: r.data.temp_password });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    }
    setBusy(null);
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this agent application? They will not be able to log in.')) return;
    setBusy(id);
    try {
      await api().post(`/ebn/admin/agents/${id}/reject`, { reason: '' });
      toast.success('Agent rejected.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reject');
    }
    setBusy(null);
  };

  const copyPwd = () => {
    if (tempPwd?.password) {
      navigator.clipboard.writeText(tempPwd.password);
      toast.success('Temporary password copied');
    }
  };

  return (
    <div className="space-y-6" data-testid="pending-agents-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pending Agent Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Agents who signed up via <code className="bg-muted px-1 rounded text-xs">/register?as=agent</code> awaiting your approval. Approve to send them a welcome email with login credentials.
          </p>
        </div>
        <button onClick={load} className="text-sm font-semibold text-[#F5A623] hover:underline inline-flex items-center gap-1.5" data-testid="pending-agents-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {tempPwd && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="temp-password-banner">
          <AlertCircle className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-[#1A2A4F]">Welcome email could not be sent — share these credentials manually:</p>
            <div className="mt-2 text-sm space-y-1">
              <p><strong>Email:</strong> <code className="bg-white px-2 py-0.5 rounded border">{tempPwd.email}</code></p>
              <p><strong>Temporary password:</strong> <code className="bg-white px-2 py-0.5 rounded border">{tempPwd.password}</code>
                <button onClick={copyPwd} className="ml-2 text-xs text-[#F5A623] font-semibold inline-flex items-center gap-1" data-testid="copy-temp-password">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </p>
              <p className="text-xs text-gray-600 mt-1">Configure SMTP at <a href="/integrations/smtp" className="underline text-[#F5A623]">Integrations → SMTP</a> to send these automatically.</p>
            </div>
          </div>
          <button onClick={() => setTempPwd(null)} className="text-xs text-gray-500 hover:text-[#1A2A4F]" data-testid="dismiss-temp-pw">Dismiss</button>
        </div>
      )}

      {loading ? (
        <p className="text-center text-muted-foreground py-12">Loading…</p>
      ) : agents.length === 0 ? (
        <div className="bg-gradient-to-br from-[#F5A623]/10 to-white border border-dashed border-[#F5A623]/30 rounded-2xl py-16 px-4 text-center" data-testid="pending-agents-empty">
          <CheckCircle2 className="w-12 h-12 text-[#F5A623] mx-auto mb-3" />
          <h3 className="text-lg font-bold text-foreground">No pending agent applications</h3>
          <p className="mt-2 text-sm text-muted-foreground">All caught up! New agent signups will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pending-agents-grid">
          {agents.map((a) => (
            <div key={a.id} className="bg-card border border-border rounded-xl p-5" data-testid={`pending-agent-${a.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center font-bold shrink-0">
                    {(a.name || a.username || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-foreground truncate">{a.name || a.username}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium shrink-0">Pending</span>
              </div>

              <div className="space-y-1.5 text-sm">
                <p className="flex items-center gap-2 text-foreground"><Mail className="w-3.5 h-3.5 text-[#F5A623] shrink-0" /><span className="truncate">{a.email}</span></p>
                {a.mobile && <p className="flex items-center gap-2 text-foreground"><Phone className="w-3.5 h-3.5 text-[#F5A623] shrink-0" />{a.mobile}</p>}
                {a.agency_name && <p className="flex items-center gap-2 text-foreground"><Building2 className="w-3.5 h-3.5 text-[#F5A623] shrink-0" /><span className="truncate">{a.agency_name}</span></p>}
                {a.allocated_countries?.length > 0 && (
                  <p className="flex items-start gap-2 text-foreground"><Globe2 className="w-3.5 h-3.5 text-[#F5A623] mt-0.5 shrink-0" /><span className="text-xs">{a.allocated_countries.join(', ')}</span></p>
                )}
                {a.allocated_universities?.length > 0 && (
                  <p className="flex items-start gap-2 text-foreground"><GraduationCap className="w-3.5 h-3.5 text-[#F5A623] mt-0.5 shrink-0" /><span className="text-xs">{a.allocated_universities.join(', ')}</span></p>
                )}
              </div>

              <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                <button
                  onClick={() => approve(a.id, a.email)}
                  disabled={busy === a.id}
                  className="flex-1 bg-[#F5A623] text-white text-sm font-semibold px-3 py-2 rounded-md hover:brightness-95 disabled:opacity-60 inline-flex items-center justify-center gap-1.5 transition"
                  data-testid={`approve-agent-${a.id}`}
                >
                  {busy === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Approve
                </button>
                <button
                  onClick={() => reject(a.id)}
                  disabled={busy === a.id}
                  className="flex-1 border border-red-200 text-red-700 hover:bg-red-50 text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-60 inline-flex items-center justify-center gap-1.5 transition"
                  data-testid={`reject-agent-${a.id}`}
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingAgentsPage;
